import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone } from 'lucide-react';
import BookingWizard from '../site/BookingWizard';
import RatesModal from '../site/RatesModal';
import Picture from '../site/Picture';
import { FARES, PHONE_HREF, PHONE_NUMBER } from '../../siteConfig';

const euro = (n) => `€${n}`;

// The route behind the fare, drawn the way the live site does it: a faint
// line that the yellow route draws over from one end to the other, then the
// end dot fills. Labels are real text so they translate and wrap.
function RouteLine({ from, to }) {
  return (
    <p className="m-0 flex max-w-xl items-center gap-3 text-sm font-semibold uppercase tracking-[0.14em] text-slate-300 sm:text-base">
      <span className="whitespace-nowrap">{from}</span>
      <span aria-hidden="true" className="flex min-w-[48px] flex-1 items-center gap-1">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-taxi" />
        <svg viewBox="0 0 100 2" preserveAspectRatio="none" focusable="false" className="block h-0.5 min-w-0 flex-1 overflow-visible">
          <line x1="0" y1="1" x2="100" y2="1" stroke="rgba(255,255,255,0.18)" strokeWidth="2" />
          <line className="fare-route-draw" x1="0" y1="1" x2="100" y2="1" stroke="#F2C94C" strokeWidth="2" pathLength="1" />
        </svg>
        <span className="fare-route-dot-end h-2.5 w-2.5 shrink-0 rounded-full border-2 border-solid border-taxi bg-transparent" />
      </span>
      <span className="whitespace-nowrap">{to}</span>
    </p>
  );
}

function Hero() {
  const { t } = useTranslation();
  const [direction, setDirection] = useState('toCity');
  const [places, setPlaces] = useState(() => ({ pickup: t('home.hero.placeAirport'), dropoff: '' }));
  const [ratesOpen, setRatesOpen] = useState(false);

  const toCity = direction === 'toCity';
  const airport = t('home.hero.routeFrom');
  const city = t('home.hero.routeTo');

  const changeDirection = (next) => {
    if (next === direction) return;
    setDirection(next);
    // The same two places, the other way round.
    setPlaces((p) => ({ pickup: p.dropoff, dropoff: p.pickup }));
  };

  const scrollToForm = () => {
    setRatesOpen(false);
    const form = document.getElementById('book');
    form?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    // Fills exactly one screen below the header, so the fare and the whole
    // booking card are visible without scrolling.
    <section id="hero" className="relative isolate flex min-h-[calc(100svh-64px)] items-center overflow-hidden bg-navy py-5 font-sans [@media(max-height:760px)]:py-3 text-white lg:min-h-[calc(100svh-72px)] lg:pb-28 lg:pt-6">
      <Picture
        name="fleet-three-taxis-banner"
        alt=""
        width={1086}
        height={611}
        eager
        shimmer={false}
        className="absolute inset-0 -z-10 h-full w-full"
        imgClassName="object-[50%_75%]"
      />
      <div aria-hidden="true" className="absolute inset-0 -z-10 bg-navy/[0.82] lg:bg-transparent lg:bg-gradient-to-r lg:from-navy lg:via-navy/[0.88] lg:to-navy/60" />
      <div className="site-container relative grid grid-cols-1 items-center gap-5 [@media(max-height:760px)]:gap-3 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-7">
          {/* Keyed on direction so the entrance replays when the route is flipped. */}
          <RouteLine key={`route-${direction}`} from={toCity ? airport : city} to={toCity ? city : airport} />
          <h1 key={`fare-${direction}`} className="fare-figure m-0 mt-2 text-[72px] font-extrabold [@media(max-height:760px)]:text-[52px] leading-[0.9] tracking-[-0.04em] text-taxi sm:text-[112px] md:text-[136px] lg:mt-4 lg:text-[160px] lg:[@media(max-height:760px)]:text-[120px]">
            <span className="sr-only">{t('site.hero.priceLabel', { from: toCity ? airport : city, to: toCity ? city : airport })} </span>
            {euro(toCity ? FARES.toCity : FARES.toAirport)}
          </h1>
          <p className="mb-0 mt-3 max-w-xl text-base leading-normal text-slate-200 [@media(max-height:760px)]:mt-2 lg:mt-6 lg:text-lg lg:leading-relaxed">
            {t('home.hero.fareNote')}{' '}
            <span className="[@media(max-height:760px)]:hidden">{t('home.hero.meet')}</span>
          </p>
          <p className="mb-0 mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-base text-white lg:mt-4 lg:text-lg">
            <span className="font-semibold">
              {toCity
                ? t('home.hero.returnFare', { price: euro(FARES.toAirport) })
                : t('site.hero.otherWay', { price: euro(FARES.toCity) })}
            </span>
            <span aria-hidden="true" className="text-slate-300">·</span>
            <button
              type="button"
              onClick={() => setRatesOpen(true)}
              className="focus-ring-dark cursor-pointer rounded border-0 bg-transparent p-0 font-sans text-base font-semibold text-taxi underline decoration-taxi/40 underline-offset-4 hover:decoration-taxi lg:text-lg"
            >
              {t('home.hero.seeRates')}
            </button>
          </p>
        </div>

        <div id="book" className="scroll-mt-4 lg:col-span-5">
          <BookingWizard
            direction={direction}
            onDirectionChange={changeDirection}
            places={places}
            onPlacesChange={setPlaces}
          />
          <a
            href={PHONE_HREF}
            className="focus-ring-dark group mt-3 flex min-h-[48px] items-center gap-3 rounded-btn border border-solid border-white/15 bg-white/[0.06] px-3 py-2 text-white no-underline transition-colors duration-150 hover:border-white/30 hover:bg-white/10 [@media(max-height:760px)]:mt-2"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-taxi text-navy">
              <Phone size={16} aria-hidden="true" />
            </span>
            <span className="text-sm text-slate-300">{t('site.hero.callPrompt')}</span>
            <span className="ml-auto text-base font-bold tracking-wide group-hover:text-taxi">{PHONE_NUMBER}</span>
          </a>
        </div>
      </div>

      <RatesModal open={ratesOpen} onClose={() => setRatesOpen(false)} onBook={scrollToForm} />
    </section>
  );
}

export default Hero;
